import React from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { useRouteMatch } from "react-router-dom";

import {
    mobileHeaderHeight,
    desktopHeaderHeight
} from "core/SiteHeader/constants";
import breakpoints from "core/breakpoints";
import fonts from "core/fonts";
import { A, ButtonLink } from "core/Typography";
import colors from "core/colors";

import { PHOTO_CHECKOUT, ICON_CHECKOUT } from "data/links";

const WITH_TOP_BANNER_DESKTOP = desktopHeaderHeight + 50;
const WITH_TOP_BANNER_MOBILE = mobileHeaderHeight + 50;

/*************************************************************************
 *
 *
 * NOTE ABOUT THIS FILE:
 * use core/SectionContainer instead of any of these "Sections" -
 * we will deprecate any of the "Section*" components soon
 *
 *
 *************************************************************************/

const Layout = styled.div`
    width: 100%;
    max-width: ${({ containerMaxWidth }) =>
        containerMaxWidth}; /* TODO: fix this properly */
    margin-left: auto;
    margin-right: auto;

    /* for sticky footer */
    display: flex;
    min-height: 100vh;
    flex-direction: column;
`;

// for sticky footer
export const compensateForStickyHeader = css`
    margin-top: ${mobileHeaderHeight}px;
    @media (min-width: ${breakpoints.md}rem) {
        margin-top: ${desktopHeaderHeight}px; /* compensates for sticky header */
    }
`;

const StyledContentShifterForStickyHeader = styled.div`
    ${({ hide }) => hide && "display: none"};
    ${({ heroSearchBgColor }) =>
        `background-color: ${
            heroSearchBgColor ? heroSearchBgColor : colors.snow
        };`}
    min-height: ${mobileHeaderHeight}px;
    
    .with-top-banner & {
        min-height: ${WITH_TOP_BANNER_MOBILE}px;
    }
    
    @media (min-width: ${breakpoints.tabletPx}px) {
        min-height: ${desktopHeaderHeight}px;

        .with-top-banner & {
            min-height: ${WITH_TOP_BANNER_DESKTOP}px;
        }
    }
`;

StyledContentShifterForStickyHeader.propTypes = {
    hide: PropTypes.bool
};

const ContentShifterForStickyHeader = ({ className }) => {
    const onPhotoCheckoutPage = useRouteMatch(PHOTO_CHECKOUT);
    const onIconCheckoutPage = useRouteMatch(ICON_CHECKOUT);

    return (
        <StyledContentShifterForStickyHeader
            className={className}
            hide={Boolean(onPhotoCheckoutPage || onIconCheckoutPage)}
        />
    );
};

ContentShifterForStickyHeader.propTypes = {
    className: PropTypes.string
};

const SiteContentWrapper = styled.div`
    flex: 1 1 auto;
`;

export const fullWidthStyles = css`
    width: 100vw;
    position: relative;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
`;

const removeFullWidthStyles = css`
    width: 100%;
    position: static;
    left: 0;
    right: 0;
    margin-left: auto;
    margin-right: auto;
`;

const getPaddingY = (paddingY, screenSize) =>
    paddingY &&
    css`
        padding-top: ${paddingY[screenSize]}rem;
        padding-bottom: ${paddingY[screenSize]}rem;
    `;

/*************************************************************************
 *
 *
 * NOTE ABOUT THIS FILE:
 * use core/SectionContainer instead of any of these "Sections" -
 * we will deprecate any of the "Section*" components soon
 *
 *
 *************************************************************************/
const Section = styled.section`
    width: 100%;
    padding-left: 0.9375rem;
    padding-right: 0.9375rem;

    ${({ fullWidth }) => (fullWidth === "sm" || fullWidth) && fullWidthStyles}
    ${({ removeFullWidth }) =>
        removeFullWidth === "sm" && removeFullWidthStyles}

    ${({ paddingY }) => getPaddingY(paddingY, "sm")}

    @media (min-width: ${breakpoints.md}rem) {
        padding-left: 1.25rem;
        padding-right: 1.25rem;

        ${({ paddingY }) => getPaddingY(paddingY, "md")}

        ${({ fullWidth }) => fullWidth === "md" && fullWidthStyles}
        ${({ removeFullWidth }) =>
            removeFullWidth === "md" && removeFullWidthStyles}
    }

    @media (min-width: ${breakpoints.lg}rem) {
        padding-left: 1.5rem;
        padding-right: 1.5rem;

        ${({ paddingY }) => getPaddingY(paddingY, "lg")}

        ${({ fullWidth }) => fullWidth === "lg" && fullWidthStyles}
        ${({ removeFullWidth }) =>
            removeFullWidth === "lg" && removeFullWidthStyles}
    }
`;

/*************************************************************************
 *
 *
 * NOTE ABOUT THIS FILE:
 * use core/SectionContainer instead of any of these "Sections" -
 * we will deprecate any of the "Section*" components soon
 *
 *
 *************************************************************************/
const SectionDark = styled(Section)`
    background-color: ${colors.midnight};
    color: ${fonts.fontColors.light.primary};

    ${A}, ${ButtonLink} {
        &,
        &:active,
        &:hover,
        &:visited {
            color: ${fonts.fontColors.light.link};
        }
    }
`;

/*************************************************************************
 *
 *
 * NOTE ABOUT THIS FILE:
 * use core/SectionContainer instead of any of these "Sections" -
 * we will deprecate any of the "Section*" components soon
 *
 *
 *************************************************************************/
const SectionWhite = styled(Section)`
    background-color: ${colors.snow};
    color: ${colors.black};

    ${A}, ${ButtonLink} {
        &,
        &:active,
        &:hover,
        &:visited {
            color: ${colors.fossil};
        }
    }

    label {
        color: ${colors.storm};
    }
`;

/*************************************************************************
 *
 *
 * NOTE ABOUT THIS FILE:
 * use core/SectionContainer instead of any of these "Sections" -
 * we will deprecate any of the "Section*" components soon
 *
 *
 *************************************************************************/
const SectionGray = styled(Section)`
    background-color: #f2f2f2;
    color: ${colors.black};

    ${A}, ${ButtonLink} {
        &,
        &:active,
        &:hover,
        &:visited {
            color: ${colors.fossil};
        }
    }
`;

export {
    Layout,
    Section,
    SectionDark,
    SectionGray,
    SectionWhite,
    SiteContentWrapper,
    ContentShifterForStickyHeader
};
